import type Decimal from 'decimal.js';

import * as braintree from 'braintree-web';

import type { VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';
import { isAccountAllowed } from '@hotjar/webapp/domains/common/feature';
import { experimentService, VARIANTS } from '@hotjar/experimentation';

import type { OwnedAccount } from '@hotjar/account';

import { setupClient } from './common';
import {
  authenticationModalClosed,
  authenticationModalCustomerCancelled,
  authenticationModalRendered,
  authenticationNeeded,
  payment3DS2AuthenticationLookup,
  verify3DSCalled as trackVerify3DSCalled,
  verify3DSChallenge as trackVerify3DSChallenge,
} from '../utils/tracking';

let instance: braintree.ThreeDSecure | null = null;

/**
 * setup a braintree 3dS instance
 */
export async function setup3DSInstance(account: OwnedAccount): Promise<braintree.ThreeDSecure> {
  /**
   * Only one instance of 3ds can be instantiated for particular user,
   *  since user can be account owner of one account
   * We cache the instance mainly due to the fact that events are emitted multiple times,
   *  when multiple instances are created for one client
   */
  if (instance) {
    return instance;
  }

  const client = await setupClient(account.id);

  instance = await braintree.threeDSecure.create({ client, version: 2 });

  instance.on('authentication-modal-close', () => {
    authenticationModalClosed(account);
  });

  instance.on('authentication-modal-render', () => {
    authenticationModalRendered(account);
    // TODO: 👇 re-implement datadog RUM to:
    // measure how much time it takes from starting the authentication flow until
    // the 3DS modal challenge is rendered
  });

  instance.on('customer-canceled', () => {
    authenticationModalCustomerCancelled(account);
  });

  instance.on('lookup-complete', (data, next) => {
    const { paymentMethod } = data as braintree.VerificationData;
    payment3DS2AuthenticationLookup({
      account,
      reportedStatus: paymentMethod?.threeDSecureInfo?.status ?? 'no_status_reported',
      bin: paymentMethod?.details?.bin,
      threeDSecureVersion: paymentMethod?.threeDSecureInfo?.threeDSecureVersion,
    });

    next();
  });

  return instance;
}

// Get experiment state and track it
//  - in case the the challenge is already forced by a feature flag, don't include this case in the experiment
export async function getChallengeRequestExperiment(
  account: OwnedAccount,
  shouldRequestChallenge: boolean,
): Promise<boolean> {
  const experimentId = 'pay_request_3ds_challenge';
  if (shouldRequestChallenge) {
    return true;
  }

  try {
    const { variant } = await experimentService.startAccountExperiment(experimentId, account);
    return variant === VARIANTS.variant1;
  } catch {
    return false;
  }
}

/**
 * Verify the card, if necessary.
 *
 * This will determine whether a 3D Secure verification is necessary and request it from the bank. If the bank
 * deems it necessary to show a 3DS page, we will display this to the end user.
 *
 * This method also handles the response to the 3DS challenge. If the challenge was successful, we resolve with
 * the new payment method (and updated nonce) from the 3DS challenge. If it was not (usually because the user
 * hit cancel), then we return a rejected promise containing the updated payment method. This payment method
 * will still have an updated nonce. We can use this to attempt to perform a transaction, however it has a
 * higher risk of being rejected by the bank.
 *
 * If we pass in a total value of "0.00", then we do not attempt to 3DS verify the transaction and just return
 * the payment method we passed in.
 *
 */
export async function verify3DS(
  vaultedPaymentMethod: VaultedPaymentMethod,
  total: Decimal,
  account: OwnedAccount,
): Promise<VaultedPaymentMethod> {
  const shouldRequestChallenge = isAccountAllowed('force_3ds_challenge', account);

  if (!total.eq(0)) {
    trackVerify3DSCalled({
      amountCharged: total.toString(),
      bin: vaultedPaymentMethod.bin,
      account,
    });

    const challengeRequested = await getChallengeRequestExperiment(account, shouldRequestChallenge);

    const { nonce, threeDSecureInfo } = await (instance as braintree.ThreeDSecure).verifyCard({
      amount: total.toFixed(2),
      nonce: vaultedPaymentMethod.nonce,
      bin: vaultedPaymentMethod.bin,
      challengeRequested,
    });

    const { liabilityShiftPossible, liabilityShifted, threeDSecureVersion } = threeDSecureInfo;
    const verifiedPaymentMethod = vaultedPaymentMethod.getVerifiedPaymentMethod(nonce);

    trackVerify3DSChallenge({
      amountCharged: total.toBinary(),
      eligibleFor3DS: liabilityShiftPossible,
      passed3DS: liabilityShifted,
      threeDSecureVersion,
      account,
    });

    const authenticated = !liabilityShiftPossible || liabilityShifted;

    // TODO: 👇 re-implement datadog RUM to:
    // window.datadogRum.addAction('authenticate-card'

    return authenticated ? verifiedPaymentMethod : Promise.reject(verifiedPaymentMethod);
  }

  // 3DS isn't required (and can't be verified anyway) on 0-value subscriptions. In this case, the user is
  // probably churning out so we want the upsert to continue.
  return vaultedPaymentMethod;
}

/**
 * Authenticate the given payment method against the calculated subscription value.
 *
 * This will only attempt to authenticate credit cards. All other payment methods will simply pass through this
 * function as they are either authenticated elsewhere (in the case of PayPal)
 *
 */
export async function authenticatePaymentMethod(
  account: OwnedAccount,
  vaultedPaymentMethod: VaultedPaymentMethod,
  amount: Decimal,
): Promise<VaultedPaymentMethod> {
  const isAuthenticationNeeded = isAccountAllowed('billing_3ds2', account);

  authenticationNeeded({ account, billing3DS2Enabled: isAuthenticationNeeded });

  if (!isAuthenticationNeeded || vaultedPaymentMethod?.paymentType !== 'credit_card') {
    // 3ds not enabled for this account yet
    // OR
    // We only support authenticating credit card payment methods.
    // Other payment method types are either handled by different
    // methods or are excluded from authentication.
    return vaultedPaymentMethod;
  }

  const authenticateRequest = setup3DSInstance(account)
    .then(
      () => verify3DS(vaultedPaymentMethod, amount, account),
      // If any of the pre-requisite steps fail, simply
      // resolve with the given payment method.
      //
      // This allows the flow to continue, just without
      // 3DS verification.
      () => vaultedPaymentMethod,
    )
    // If 3DS fails to verify, simply resolve with the resulting
    // payment method. This is needed as `verify3DS` consumes
    // the nonce held within `vaultedPaymentMethod` and returns a new
    // usable nonce, even if the verification failed.
    //
    // This allows the flow to continue, just without 3DS
    // verification.
    .then(undefined, (paymentMethod) => paymentMethod);

  return authenticateRequest;
}
